import { useLayoutEffect } from 'react';

// The map cross-fades over the cluster's page on View Transitions; without them, the page just changes.
export type PageTransitionKind = 'open-map' | 'close-map';

export interface TransitionPoint {
  x: number;
  y: number;
}

/** Marks the button a map opens from; closing, the map folds back into it. */
export const MAP_ENTRY_ATTRIBUTE = 'data-map-entry';

const OPEN_MS = 520;
const CLOSE_MS = 400;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';
// The arriving page settles from a touch closer; the one it covers eases back a hair.
const ARRIVE_SCALE = 1.015;
const RECEDE_SCALE = 0.99;
// The browser gives up on a new page after about four seconds; ours never waits that long.
const READY_TIMEOUT_MS = 1500;

let markReady: (() => void) | null = null;
let running: Promise<void> = Promise.resolve();

export const centreOf = (element: Element | null): TransitionPoint | null => {
  if (!element) return null;
  const { left, top, width, height } = element.getBoundingClientRect();
  return { x: left + width / 2, y: top + height / 2 };
};

const middleOfScreen = (): TransitionPoint => ({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

/** Whether a page is arriving through a transition right now (its first render can skip its own entrance). */
export const isPageTransitionRunning = () => Boolean(document.documentElement.dataset.pageTransition);

/** Settles once the running transition has played out (at once when none is running). */
export const pageTransitionDone = () => running;

// Only the page on top fades, so the one under it stays opaque and nothing dips through to the background.
function play(kind: PageTransitionKind, at: TransitionPoint) {
  const html = document.documentElement;
  // Scaled about the button, the map seems to come out of it and go back into it.
  const origin = `${at.x}px ${at.y}px`;
  const opening = kind === 'open-map';
  const timing = {
    duration: opening ? OPEN_MS : CLOSE_MS,
    easing: opening ? EASE_OUT : EASE_IN_OUT,
    fill: 'both' as const,
  };

  html.animate(
    {
      opacity: opening ? [0, 1] : [1, 0],
      transform: opening ? [`scale(${ARRIVE_SCALE})`, 'scale(1)'] : ['scale(1)', `scale(${ARRIVE_SCALE})`],
      transformOrigin: [origin, origin],
    },
    { ...timing, pseudoElement: opening ? '::view-transition-new(root)' : '::view-transition-old(root)' },
  );
  html.animate(
    {
      transform: opening ? ['scale(1)', `scale(${RECEDE_SCALE})`] : [`scale(${RECEDE_SCALE})`, 'scale(1)'],
      transformOrigin: [origin, origin],
    },
    { ...timing, easing: EASE_OUT, pseudoElement: opening ? '::view-transition-old(root)' : '::view-transition-new(root)' },
  );
}

interface PageTransition {
  kind: PageTransitionKind;
  /** Changes the page, usually a navigation. The transition waits for the new page's usePageTransitionReady. */
  update: () => void;
  // Opening, the button pressed. Closing, the entry button is found by itself; this is the fallback.
  origin?: TransitionPoint | null;
}

export function startPageTransition({ kind, update, origin }: PageTransition) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (typeof document.startViewTransition !== 'function' || reduceMotion) {
    update();
    return;
  }
  const html = document.documentElement;
  html.dataset.pageTransition = kind;
  html.style.setProperty('--page-transition-ms', `${kind === 'open-map' ? OPEN_MS : CLOSE_MS}ms`);
  html.style.setProperty('--page-transition-ease', kind === 'open-map' ? EASE_OUT : EASE_IN_OUT);

  const transition = document.startViewTransition(
    () =>
      new Promise<void>(resolve => {
        const timer = window.setTimeout(() => markReady?.(), READY_TIMEOUT_MS);
        markReady = () => {
          window.clearTimeout(timer);
          markReady = null;
          resolve();
        };
        update();
      }),
  );

  transition.ready
    .then(() => {
      const at =
        kind === 'close-map'
          ? centreOf(document.querySelector(`[${MAP_ENTRY_ATTRIBUTE}]`)) ?? origin
          : origin;
      play(kind, at ?? middleOfScreen());
    })
    // Skipped (another transition took over, or the tab was hidden): the page has changed all the same.
    .catch(() => undefined);
  running = transition.finished
    .catch(() => undefined)
    .finally(() => {
      delete html.dataset.pageTransition;
      html.style.removeProperty('--page-transition-ms');
      html.style.removeProperty('--page-transition-ease');
    });
}

/**
 * Called by a page a transition can lead to: once it has rendered what it opens with, the browser
 * pictures it and the transition plays.
 */
export function usePageTransitionReady(ready = true) {
  useLayoutEffect(() => {
    if (ready) markReady?.();
  }, [ready]);
}
